import { FormGroup, Layer, Stack } from "@carbon/react";
import { zodResolver } from "@hookform/resolvers/zod";
import React, { useMemo } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { any, object, string, record } from "zod";

import { TextAreaFormField } from "src/components/Form/TextAreaFormField.tsx";
import { TextInputFormField } from "src/components/Form/TextInputFormField.tsx";
import { ConfirmActionModal } from "src/components/Modal";
import { IdentifierFilterableSinglePickerFormField } from "src/components/Pickers/IdentifierFilterableSinglePickerFormField";
import { useToast } from "src/components/Toasts/ToastContainer.tsx";
import { CODE_REGEX } from "src/constants/common";
import { mapToModalError } from "src/errors";
import { getRulesQuery } from "src/graphql/anti-fraud/queries/rules/GetRulesQuery.ts";
import { createRuleMutation } from "src/graphql/anti-fraud/queries/rules/mutations/CreateRuleMutation.ts";
import { usePerformMutation } from "src/graphql/utils";
import { useT } from "src/internationalization";
import { ClientSchemaBlacklist, ClientSchemaIdentifier } from "src/types/clientSchema";
import { Rule } from "src/types/rule";

import { BlacklistFilterableSinglePickerFormField } from "./BlacklistFilterableSinglePickerFormField";

export type CreateRuleFormPayload = {
  code: string;
  name: string;
  description: string;
  listElementType: ClientSchemaBlacklist | null;
  identifier: ClientSchemaIdentifier | null;
};

type CreateRuleModalProps = {
  onClose: () => void;
  caseTypeCode: string;
  rules: Rule[];
};

export const CreateRuleModal: React.FC<CreateRuleModalProps> = ({
  onClose,
  caseTypeCode,
  rules,
}) => {
  const t = useT();
  const { showSuccessToast } = useToast();
  const validationSchema = useSchema(rules);

  const methods = useForm<CreateRuleFormPayload>({
    defaultValues: {
      code: "",
      name: "",
      description: "",
      identifier: null,
      listElementType: null,
    },
    resolver: zodResolver(validationSchema),
  });

  const { watch, handleSubmit } = methods;
  const identifier = watch("identifier");
  const listType = watch("listElementType");

  const {
    performMutation: createRule,
    loading,
    error,
  } = usePerformMutation(createRuleMutation, {
    onSuccess: () => {
      showSuccessToast(t("page.rules.modal.create.toast.success.message"));
      onClose();
    },
    mutationOption: {
      refetchQueries: [getRulesQuery],
      awaitRefetchQueries: true,
    },
  });

  const onSubmit: SubmitHandler<CreateRuleFormPayload> = async (data) => {
    await createRule({
      code: data.code,
      name: data.name,
      description: data.description,
      listElementType: data.listElementType!.code,
      caseTypeCode: caseTypeCode,
      identifierPath: {
        elementPath: [data.identifier!.elementCode],
        identifierCode: data.identifier!.identifierTypeCode,
      },
    });
  };

  return (
    <ConfirmActionModal
      error={mapToModalError(error)}
      onClose={onClose}
      size="md"
      loading={loading}
      title={t("page.rules.modal.create.title")}
      confirmButtonText={t("general.actions.confirm")}
      onConfirm={handleSubmit(onSubmit)}
      initialFocus="#name"
    >
      <FormProvider {...methods}>
        <Stack gap={5}>
          <TextInputFormField
            id="name"
            name="name"
            control={methods.control}
            label={t("general.form.name.label")}
            helperText={t("page.rules.modal.create.form.name.helper")}
          />
          <TextInputFormField
            id="code"
            name="code"
            control={methods.control}
            label={t("general.form.code.label")}
            helperText={t("page.rules.modal.create.form.code.helper")}
          />

          <TextAreaFormField
            id="description"
            label={t("general.form.description.label")}
            helperText={t("page.rules.modal.create.form.description.helper")}
            name="description"
            control={methods.control}
          />
          <FormGroup legendText={t("page.rules.modal.create.form.group.construction.legend")}>
            <Layer level={1}>
              <div className="flex items-center">
                <div className="flex-1 gap-5">
                  <IdentifierFilterableSinglePickerFormField
                    id="identifier"
                    name="identifier"
                    label={t("page.rules.modal.create.form.type.element.title")}
                    helperText={t("page.rules.modal.create.form.type.element.helper")}
                    caseTypeCode={caseTypeCode}
                  />
                </div>
                <div className="mx-7">{t("page.rules.modal.create.form.type.element.join")}</div>
                <div className="flex-1">
                  <BlacklistFilterableSinglePickerFormField
                    name="listElementType"
                    helperText={t("page.rules.modal.create.form.type.blacklist.helper")}
                    label={t("page.rules.modal.create.form.type.blacklist.title")}
                    id="blacklist"
                  />
                </div>
              </div>
            </Layer>
          </FormGroup>
          <Stack gap={5} className="mt-7">
            <h4>{t("page.rules.modal.create.summary.title")}</h4>
            <div className="flex">
              <div className="mr-5">{t("page.rules.modal.create.summary.description")}:</div>
              {identifier && listType && (
                <>
                  <div className="text-backgroundBrand">
                    {identifier.elementName} - {identifier.identifierName}
                  </div>
                  <div className="mx-5">
                    {t("page.rules.modal.create.summary.type.element.label")}
                  </div>
                  <div className="text-backgroundBrand">{listType.name}</div>
                </>
              )}
            </div>
          </Stack>
        </Stack>
      </FormProvider>
    </ConfirmActionModal>
  );
};

const useSchema = (rules: Rule[]) => {
  const t = useT();

  return useMemo(() => {
    return object({
      name: string()
        .min(1, t("general.form.name.error.required"))
        .refine((value) => !rules.some((rule) => rule.name === value), {
          message: t("general.form.name.error.unique"),
        }),
      code: string()
        .min(1, t("general.form.code.error.required"))
        .regex(CODE_REGEX, t("general.form.code.error.invalid"))
        .refine((value) => !rules.some((rule) => rule.code === value), {
          message: t("general.form.code.error.unique"),
        }),
      description: string().min(1, t("general.form.description.error.required")),
      identifier: record(any(), { message: t("general.form.field.required") }),
      listElementType: record(any(), { message: t("general.form.field.required") }),
    });
  }, [rules, t]);
};
